import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Clock, Rocket, Star } from 'lucide-react';
import SEO from '../components/SEO';
import RelatedPages from '../components/RelatedPages';

const fade   = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } };
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };

/* ── Datos de tendencias ── */
const TRENDS = [
  {
    stat: '70 %',
    label: 'de los sistemas multiagente desplegarán agentes con roles especializados para 2027',
    source: 'Gartner, Hype Cycle for AI, 2025',
    color: 'text-cyan-300',
    bg: 'bg-cyan-400/8',
    border: 'border-cyan-400/25',
  },
  {
    stat: '82 %',
    label: 'de los líderes empresariales planean aumentar el número de agentes IA en los próximos 12 meses',
    source: 'Microsoft Work Trend Index, 2025',
    color: 'text-violet-300',
    bg: 'bg-violet-400/8',
    border: 'border-violet-400/25',
  },
  {
    stat: '29 min',
    label: 'tiempo medio de acceso a infraestructuras empresariales. Bajó de 62 min en 2023',
    source: 'CrowdStrike Global Threat Report, 2025',
    color: 'text-red-300',
    bg: 'bg-red-400/8',
    border: 'border-red-400/25',
  },
  {
    stat: '73,3 %',
    label: 'de los intentos de acceso inicial comenzaron por una persona engañada o cuenta comprometida',
    source: 'Verizon DBIR, 2025',
    color: 'text-amber-300',
    bg: 'bg-amber-400/8',
    border: 'border-amber-400/25',
  },
];

/* ── Hoja de ruta ── */
const ROADMAP = [
  {
    id: 'app-movil',
    period: 'En diseño',
    title: 'App Móvil Aynara',
    desc: 'Control de agentes, aprobación de acciones, alertas de seguridad y activación de flujos desde el móvil. Diseñada para dirección y operaciones.',
    status: 'design',
    link: '/app-movil',
    linkLabel: 'Ver lista de espera',
    color: 'text-amber-300',
    border: 'border-amber-400/25',
    bg: 'bg-amber-400/6',
    dotColor: 'bg-amber-400',
  },
  {
    id: 'conectores',
    period: 'Próximamente',
    title: 'Más conectores y servidores MCP',
    desc: 'Ampliar la biblioteca de herramientas conectadas: HubSpot, Salesforce, SAP, herramientas de sector público y conectores via protocolo MCP estandarizado.',
    status: 'planned',
    link: '/developers',
    linkLabel: 'Ver entorno de desarrolladores',
    color: 'text-cyan-300',
    border: 'border-cyan-400/25',
    bg: 'bg-cyan-400/6',
    dotColor: 'bg-cyan-400',
  },
  {
    id: 'agentes-sector',
    period: 'Próximamente',
    title: 'Agentes especializados por sector',
    desc: 'Paquetes de agentes preconfigurados para e-commerce, servicios profesionales, administración pública y agencias. Activación en menos de un día.',
    status: 'planned',
    link: '/soluciones',
    linkLabel: 'Ver soluciones por sector',
    color: 'text-emerald-300',
    border: 'border-emerald-400/25',
    bg: 'bg-emerald-400/6',
    dotColor: 'bg-emerald-400',
  },
  {
    id: 'certificaciones',
    period: 'En evaluación',
    title: 'Certificaciones de seguridad',
    desc: 'Evaluación de certificaciones ISO 27001 y ENS para clientes de administración pública y sectores regulados. Auditoría de los 6 pilares por terceros.',
    status: 'evaluation',
    link: '/seguridad-agentes',
    linkLabel: 'Ver pilares de seguridad',
    color: 'text-violet-300',
    border: 'border-violet-400/25',
    bg: 'bg-violet-400/6',
    dotColor: 'bg-violet-400',
  },
  {
    id: 'marketplace',
    period: 'Hoja de ruta',
    title: 'Marketplace de agentes',
    desc: 'Espacio donde partners y clientes técnicos pueden publicar agentes preconfigurados y herramientas MCP para que otros clientes los adopten sin desarrollo.',
    status: 'roadmap',
    color: 'text-slate-300',
    border: 'border-white/12',
    bg: 'bg-white/[0.02]',
    dotColor: 'bg-slate-500',
  },
  {
    id: 'mcp-extendido',
    period: 'Hoja de ruta',
    title: 'MCP extendido y AppFunctions',
    desc: 'Extensión del soporte MCP a casos de uso on-device mediante Android AppFunctions (Android 16+), permitiendo a los agentes interactuar con el dispositivo del usuario.',
    status: 'roadmap',
    link: '/developers',
    linkLabel: 'Ver documentación MCP',
    color: 'text-slate-300',
    border: 'border-white/12',
    bg: 'bg-white/[0.02]',
    dotColor: 'bg-slate-500',
  },
];

const STATUS_BADGE = {
  design:     { label: 'En diseño',      classes: 'bg-amber-400/15  text-amber-300  border-amber-400/30' },
  planned:    { label: 'Planificado',    classes: 'bg-cyan-400/15   text-cyan-300   border-cyan-400/30' },
  evaluation: { label: 'En evaluación', classes: 'bg-violet-400/15 text-violet-300 border-violet-400/30' },
  roadmap:    { label: 'Hoja de ruta',  classes: 'bg-white/8       text-slate-400  border-white/15' },
};

export default function TendenciasPage() {
  return (
    <>
      <SEO
        title="Tendencias y Hoja de Ruta 2026-2027 — Neuralia 369"
        description="Tendencias verificables 2026 de Gartner, Microsoft, Verizon y CrowdStrike. Hoja de ruta de Neuralia 369: app móvil, conectores MCP, certificaciones y más. Actualizado octubre 2026."
        path="/tendencias"
      />

      {/* ── HERO ── */}
      <section className="relative pt-28 pb-14 bg-[#02060f] border-b border-white/6 overflow-hidden" data-testid="tendencias-hero">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-1/3 w-[500px] h-[350px] bg-violet-400/[0.04] rounded-full blur-[110px]" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={stagger} initial="hidden" animate="show">
            <motion.p variants={fade} className="editorial-kicker mb-5 text-slate-400">
              Tendencias y Hoja de Ruta · Actualizado octubre 2026
            </motion.p>
            <motion.h1 variants={fade} className="text-white font-heading text-4xl sm:text-5xl font-black leading-tight mb-5" data-testid="tendencias-title">
              Lo que viene y{' '}
              <span className="gradient-text">lo que estamos construyendo</span>
            </motion.h1>
            <motion.p variants={fade} className="text-slate-300 text-lg max-w-2xl leading-relaxed">
              Datos verificados del sector y la hoja de ruta de Neuralia 369 para 2026-2027.
              Sin proyecciones inventadas. Solo tendencias respaldadas por fuentes y hitos propios
              con estado real.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ── SECCIÓN 1: TENDENCIAS ── */}
      <section className="py-16 bg-[#030a17] border-b border-white/6" data-testid="tendencias-trends">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} className="mb-10">
            <p className="editorial-kicker mb-3 text-slate-400">Sección 1 — Contexto del mercado</p>
            <h2 className="text-white font-heading text-3xl font-black mb-3">
              Tendencias verificables 2026
            </h2>
            <p className="text-slate-400 text-base max-w-xl">
              Datos de los informes de referencia del sector. Sin extrapolaciones ni estimaciones propias.
              Cada cifra tiene su fuente.
            </p>
          </motion.div>

          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className="grid sm:grid-cols-2 gap-5">
            {TRENDS.map(({ stat, label, source, color, bg, border }) => (
              <motion.div key={stat} variants={fade} className={`rounded-2xl border ${border} ${bg} p-6`} data-testid={`trend-card-${stat.replace(' ','')}`}>
                <p className={`text-3xl font-black mb-2 ${color}`}>{stat}</p>
                <p className="text-slate-300 text-sm leading-relaxed mb-3">{label}</p>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{source}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* Contexto narrativo */}
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} className="mt-8 rounded-2xl border border-white/8 bg-white/[0.02] p-6">
            <p className="text-slate-300 text-sm leading-relaxed">
              La convergencia de estos datos señala una misma dirección: la adopción de agentes de IA
              en entornos empresariales está acelerando, la superficie de ataque asociada también y
              la mayoría de las organizaciones todavía no tiene un marco de seguridad específico para agentes.
              Las empresas que establezcan ese marco en 2026 estarán mejor posicionadas para 2027.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── SECCIÓN 2: HOJA DE RUTA ── */}
      <section className="py-16 bg-[#02060f] border-b border-white/6" data-testid="tendencias-roadmap">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} className="mb-10">
            <p className="editorial-kicker mb-3 text-slate-400">Sección 2 — Hoja de ruta Neuralia 369</p>
            <h2 className="text-white font-heading text-3xl font-black mb-3">
              6 hitos en construcción
            </h2>
            <p className="text-slate-400 text-base max-w-xl">
              Estado real de cada hito. Sin fechas exactas. Sin compromisos de entrega públicos.
              Solo el estado actual y hacia dónde va cada uno.
            </p>
          </motion.div>

          {/* Timeline */}
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-4 sm:left-6 top-0 bottom-0 w-px bg-white/8" />

            <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className="space-y-4 pl-12 sm:pl-16">
              {ROADMAP.map((item, i) => {
                const badge = STATUS_BADGE[item.status];
                return (
                  <motion.div key={item.id} variants={fade} className="relative" data-testid={`roadmap-item-${item.id}`}>
                    {/* Dot */}
                    <div className={`absolute -left-[2.75rem] sm:-left-[3.25rem] top-5 w-3 h-3 rounded-full ${item.dotColor} ring-2 ring-[#02060f]`} />

                    <div className={`rounded-2xl border ${item.border} ${item.bg} p-5`}>
                      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                        <div>
                          <p className={`text-[10px] font-black uppercase tracking-widest mb-0.5 ${item.color}`}>{item.period}</p>
                          <h3 className="text-white font-bold text-base">{item.title}</h3>
                        </div>
                        <span className={`text-[9px] font-black uppercase tracking-widest border rounded-full px-2.5 py-1 ${badge.classes}`}>
                          {badge.label}
                        </span>
                      </div>
                      <p className="text-slate-400 text-sm leading-relaxed mb-3">{item.desc}</p>
                      {item.link && (
                        <Link to={item.link} className={`inline-flex items-center gap-1.5 text-xs font-semibold group transition-colors ${item.color} hover:text-white`}>
                          {item.linkLabel} <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                        </Link>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── SECCIÓN 3: VISIÓN 2027 ── */}
      <section className="py-16 bg-[#030a17] border-b border-white/6" data-testid="tendencias-vision">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} className="mb-10">
            <p className="editorial-kicker mb-3 text-slate-400">Sección 3 — Visión 2027</p>
            <h2 className="text-white font-heading text-3xl font-black mb-3">
              Dónde queremos estar en 2027
            </h2>
            <p className="text-slate-400 text-base max-w-xl">
              Esto no es un compromiso público de fechas. Es la dirección que guía nuestras decisiones
              de producto hoy.
            </p>
          </motion.div>

          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className="grid sm:grid-cols-3 gap-5">
            {[
              {
                Icon: Rocket,
                title: 'Ecosistema multiagente completo',
                desc: 'Todos los agentes del ecosistema —Ventas, Agenda, CRM, Cobros, Operaciones, Seguridad— disponibles como paquetes preconfigurados por sector, activables en menos de un día.',
                color: 'text-cyan-300',
              },
              {
                Icon: Star,
                title: 'Marketplace abierto de agentes',
                desc: 'Partners técnicos y clientes publican sus propios agentes y servidores MCP en un marketplace donde otros clientes los adoptan sin desarrollo. Un ecosistema que se extiende solo.',
                color: 'text-violet-300',
              },
              {
                Icon: CheckCircle2,
                title: 'Estándar de seguridad certificado',
                desc: 'Los 6 pilares de seguridad auditados por terceros, con certificación reconocida para clientes de sectores regulados. La seguridad deja de ser un argumento de venta y se convierte en una garantía documentada.',
                color: 'text-emerald-300',
              },
            ].map(({ Icon, title, desc, color }) => (
              <motion.div key={title} variants={fade} className="rounded-2xl border border-white/8 bg-white/[0.02] p-6">
                <Icon className={`w-7 h-7 ${color} mb-4`} />
                <h3 className="text-white font-bold text-base mb-2">{title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </motion.div>

          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} className="mt-8 rounded-2xl border border-white/8 bg-white/[0.02] p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="text-white font-black text-base mb-1">¿Quieres influir en la hoja de ruta?</p>
              <p className="text-slate-400 text-sm">Las prioridades de desarrollo las define el uso real de nuestros clientes.</p>
            </div>
            <Link to="/activar" className="btn-primary flex-shrink-0" data-testid="tendencias-cta-activar">
              Hablar con el equipo <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── Footer nota ── */}
      <div className="py-6 bg-[#02060f]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[11px] text-slate-600 text-center">
            Última actualización: octubre 2026 · Neuralia 369 · neuralia369.com
          </p>
        </div>
      </div>
      <RelatedPages pages={['sistema-multiagente','seguridad-agentes','developers','recursos']} />
    </>
  );
}
